// Native-shell Google login (passkeys/Touch ID).
//
// WKWebView may not show platform WebAuthn prompts — Apple reserves those for
// real browsers — so inside the desktop app the OAuth leg runs in a
// Safari-backed ASWebAuthenticationSession sheet instead. PKCE symmetry is the
// load-bearing detail: THIS webview's cookie jar holds the code verifier (the
// server sets it, HttpOnly, when /auth/google?native=1 answers this webview's
// fetch), the sheet only carries Google + Touch ID, and the returned ?code=
// finishes right back here via /auth/callback — the same exchange route the
// plain web flow uses.

export type NativeInvoke = (
  cmd: string,
  args?: Record<string, unknown>
) => Promise<unknown>;

type TauriWindow = Window & {
  __TAURI__?: { core?: { invoke?: NativeInvoke } };
};

/** The desktop shell's IPC entry point, when running inside it. */
export function nativeInvoke(w: Window): NativeInvoke | null {
  const invoke = (w as TauriWindow).__TAURI__?.core?.invoke;
  return typeof invoke === "function" ? invoke : null;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Ask our server for the authorize URL (the verifier lands in this webview's
 * cookies with the answer), then hand it to the shell's Safari sheet. The
 * shell finishes by navigating this webview to `${origin}/auth/callback?code=...`.
 */
export async function signInWithGoogleNative(
  origin: string,
  invoke: NativeInvoke,
  fetchFn: FetchLike = (input, init) => fetch(input, init)
): Promise<void> {
  const res = await fetchFn(
    new URL("/auth/google?native=1", origin).toString(),
    {
      credentials: "same-origin",
      cache: "no-store",
    }
  );
  const body = (await res.json().catch(() => null)) as { url?: string } | null;
  if (!res.ok || !body?.url)
    throw new Error("No authorize URL from the server");
  await invoke("native_google_login", {
    authorizeUrl: body.url,
    finishBase: new URL("/auth/callback", origin).toString(),
  });
}
